import {
    isValidElement,
    type ReactNode
} from "react";
import type {
    EmptyContentLayout,
    EmptyContentGroup,
    EmptyHeaderLayout,
    EmptyPartKind,
    EmptyType
} from "./type";
import {
    EMPTY_ACTION_DEFAULT_SIZE
} from "./stylesheet";
import type {
    NCoreUIKitIcon
} from "../../types";
import type IButtonProps from "../button/type";
import type {
    ButtonSize
} from "../button/type";
import {
    TriangleAlert as TriangleAlertIcon,
    CircleCheck as CircleCheckIcon,
    CircleAlert as CircleAlertIcon,
    Sparkles as SparklesIcon,
    Inbox as InboxIcon,
    Info as InfoIcon,
    type LucideIcon
} from "lucide-react-native";
import {
    flattenCardChildren
} from "../card/util";
import Button from "../button";

export const EMPTY_PART_KINDS: Array<EmptyPartKind> = [
    "description",
    "content",
    "header",
    "empty",
    "media",
    "title"
];

const createEmptyTypeIcon = (Icon: LucideIcon): NCoreUIKitIcon => {
    const EmptyTypeIcon: NCoreUIKitIcon = ({
        customColor,
        style,
        size
    }) => <Icon
        color={customColor}
        style={style}
        size={size}
    />;

    return EmptyTypeIcon;
};

export const EMPTY_TYPE_ICONS: Record<EmptyType, NCoreUIKitIcon> = {
    warning: createEmptyTypeIcon(TriangleAlertIcon),
    success: createEmptyTypeIcon(CircleCheckIcon),
    danger: createEmptyTypeIcon(CircleAlertIcon),
    primary: createEmptyTypeIcon(SparklesIcon),
    neutral: createEmptyTypeIcon(InboxIcon),
    info: createEmptyTypeIcon(InfoIcon)
};

const isEmptyPartKind = (value: unknown): value is EmptyPartKind => {
    return typeof value === "string" && EMPTY_PART_KINDS.some((kind) => kind === value);
};

export const getEmptyPartKind = (node: ReactNode): EmptyPartKind | undefined => {
    if(!isValidElement(node)) {
        return undefined;
    }

    const elementType: unknown = node.type;

    if(typeof elementType !== "function" && (typeof elementType !== "object" || elementType === null)) {
        return undefined;
    }

    if(!("emptyPart" in elementType)) {
        return undefined;
    }

    return isEmptyPartKind(elementType.emptyPart) ? elementType.emptyPart : undefined;
};

export const getEmptyHeaderLayout = (children: ReactNode): EmptyHeaderLayout => {
    const nodes = flattenCardChildren(children);

    return {
        medias: nodes.filter((node) => getEmptyPartKind(node) === "media"),
        texts: nodes.filter((node) => getEmptyPartKind(node) !== "media")
    };
};

const getIsEmptyLinkAction = (node: ReactNode): boolean => {
    if(!isValidElement<IButtonProps>(node) || node.type !== Button) {
        return false;
    }

    return node.props.variant === "link";
};

const getIsEmptyStackedAction = (node: ReactNode): boolean => {
    if(!isValidElement<IButtonProps>(node) || node.type !== Button) {
        return false;
    }

    return node.props.spreadBehaviour === "stretch" || node.props.spreadBehaviour === "baseline";
};

const getEmptyActionSize = (node: ReactNode): ButtonSize | undefined => {
    if(!isValidElement<IButtonProps>(node) || node.type !== Button) {
        return undefined;
    }

    return node.props.size ?? EMPTY_ACTION_DEFAULT_SIZE;
};

const getEmptyContentGroups = (actions: Array<ReactNode>): Array<EmptyContentGroup> => {
    const groups: Array<EmptyContentGroup> = [];

    actions.forEach((node) => {
        const isStacked = getIsEmptyStackedAction(node);

        const lastGroup = groups[groups.length - 1];

        if(!isStacked && lastGroup && !lastGroup.isStacked) {
            lastGroup.nodes.push(node);

            return;
        }

        groups.push({
            isStacked,
            nodes: [
                node
            ]
        });
    });

    return groups;
};

export const getEmptyContentLayout = (children: ReactNode): EmptyContentLayout => {
    const nodes = flattenCardChildren(children);

    const actions = nodes.filter((node) => !getIsEmptyLinkAction(node));

    const actionSizes: Array<ButtonSize> = [];

    actions.forEach((node) => {
        const actionSize = getEmptyActionSize(node);

        if(actionSize) {
            actionSizes.push(actionSize);
        }
    });

    return {
        links: nodes.filter((node) => getIsEmptyLinkAction(node)),
        groups: getEmptyContentGroups(actions),
        actionSizes,
        actions
    };
};
